import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  Palette,
  Armchair,
  Baby,
  Mail,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import Img from "@/components/Img";
import { STUDIO_BOOKING_URL } from "@/data/content";

const GALLERY = [
  "/studio-1.jpg",
  "/studio-2.jpg",
  "/studio-3.jpg",
  "/studio-4.jpg",
  "/studio-5.jpg",
  "/studio-6.jpg",
  "/studio-7.jpg",
  "/studio-8.jpg",
];
const STUDIO_HERO = "/studio-hero.webp";

const INCLUDED = [
  {
    icon: Armchair,
    t: "Cozy bed setup",
    d: "A styled bed area complete with layered rugs, a full-length mirror, and the signature peacock chair.",
  },
  {
    icon: Palette,
    t: "Three backdrops",
    d: "White, egg nog, and black seamless backdrops — clean canvases for any concept.",
  },
  {
    icon: Baby,
    t: "Seating & bassinets",
    d: "Movable seating options and bassinets — perfect for newborns, families, and content shoots.",
  },
];

const RATES = [
  { name: "One Hour", price: "$60", hours: "1 hour", note: "Quick sessions & headshots" },
  { name: "Two Hour", price: "$120", hours: "2 hours", note: "Family, maternity, branding" },
  { name: "Three Hour", price: "$180", hours: "3 hours", note: "Multi-look shoots" },
  {
    name: "Half-Day",
    price: "$250",
    hours: "5 hours",
    note: "Mini sessions, content shoots, or small projects",
    featured: true,
  },
];

const PAPER_ADDON = "+$15 for use of seamless paper";

export default function StudioRental() {
  return (
    <div className="pt-8 md:pt-12 bg-cream">
      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
          <div className="md:col-span-8">
            <p className="overline text-sage mb-6">Studio Rental · Lafayette / Milton, LA</p>
            <RevealText
              as="h1"
              className="font-serif font-light text-ink leading-[0.94] tracking-tight text-5xl sm:text-6xl md:text-7xl"
              lines={["A creative space,", "still becoming."]}
            />
          </div>
          <Reveal className="md:col-span-4" delay={0.15}>
            <p className="text-inksoft leading-relaxed">
              Packages starting at{" "}
              <span className="font-serif italic text-sage text-xl">$60</span>. Book by the
              hour or grab a half-day for content shoots and mini sessions.
            </p>
            <a
              href={STUDIO_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="studio-hero-book-btn"
              className="group mt-6 inline-flex items-center gap-3 rounded-full bg-ink text-cream px-8 py-4 text-sm font-medium hover:bg-sage transition-colors"
            >
              Check availability
              <ArrowRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
          </Reveal>
        </div>
      </section>

      {/* SUMMER NOTICE */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-16">
        <Reveal>
          <div
            data-testid="studio-notice"
            className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 border-b border-ink/10 pb-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-sage/40 bg-sagelight px-4 py-1.5">
              <Sun size={14} className="text-sagedark" strokeWidth={1.8} />
              <span className="overline text-sagedark">Summer update</span>
            </span>
            <p className="font-serif italic text-inksoft text-lg md:text-xl leading-snug">
              Currently only offering white paper (if needed) through the summer — everything
              else is available as usual.
            </p>
          </div>
        </Reveal>
      </section>

      {/* SEAMLESS PAPER UPDATE (temporary) */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-10">
        <Reveal>
          <div
            data-testid="studio-paper-notice"
            className="relative rounded-tl-[2.5rem] rounded-br-[2.5rem] border border-sage/40 bg-sagelight/60 p-7 md:p-9"
          >
            <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-cream border border-sage/40 px-4 py-1.5 self-start shrink-0">
                <Sparkles size={14} className="text-sagedark" strokeWidth={1.8} />
                <span className="overline text-sagedark">New — seamless paper add-on</span>
              </span>
              <div className="space-y-3 text-inksoft leading-relaxed max-w-3xl">
                <p className="font-serif italic text-ink text-lg md:text-xl leading-snug">
                  Please note: an additional fee now applies for the use of seamless paper
                  backdrops during studio rentals.
                </p>
                <p>
                  Studio rental pricing covers use of the studio space itself. Seamless paper
                  is an optional add-on and will be charged separately based on the paper used.
                </p>
                <p>
                  Please select the seamless paper add-on when booking if you plan to use it
                  during your rental.
                </p>
                <p>
                  Thank you for helping us keep studio rentals running smoothly.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ABOUT */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <Reveal className="md:col-span-6" y={60}>
          <Img
            src={STUDIO_HERO}
            alt="JCP Studio — A creative workspace for rent (collage)"
            className="aspect-[4/5] rounded-tl-[5rem]"
            imgClassName="hover:scale-105"
          />
        </Reveal>
        <Reveal className="md:col-span-6 md:pt-10" delay={0.1}>
          <p className="overline text-sage mb-4">Welcome</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-[1.05] tracking-tight">
            A growing creative home for Acadiana photographers.
          </h2>
          <div className="mt-8 space-y-5 text-inksoft leading-relaxed max-w-xl">
            <p>
              Our studio is a work in progress, but I&rsquo;m so excited to be growing this
              creative space right here in the Lafayette / Milton area! The vision is to bring
              you the best tools, props, setups, lighting, and backdrops — giving local
              photographers and creatives a place to truly bring their ideas to life.
            </p>
            <p>
              For now, rentals include access to the cozy bed area (rugs, mirror, peacock
              chair), three colored backdrops (white, egg nog, &amp; black), along with any
              movable seating options and bassinets. Don&rsquo;t worry — more is on the way as
              we continue to expand.
            </p>
          </div>

          <a
            href="mailto:info@jacquelineclairephoto.com"
            data-testid="studio-email-link"
            className="mt-8 inline-flex items-center gap-3 group text-ink"
          >
            <Mail size={18} className="text-sage" strokeWidth={1.7} />
            <span className="font-serif italic text-lg border-b border-ink/20 group-hover:border-sage group-hover:text-sage transition-colors pb-0.5">
              info@jacquelineclairephoto.com
            </span>
          </a>
        </Reveal>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-36">
        <Reveal className="mb-14 md:mb-20 max-w-2xl">
          <p className="overline text-sage mb-5">What&rsquo;s included</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-[0.98] tracking-tight">
            Everything you need in the room.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {INCLUDED.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.1}>
              <div className="border-t border-ink/15 pt-6">
                <f.icon size={30} className="text-sage" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-3xl text-ink">{f.t}</h3>
                <p className="mt-3 text-inksoft leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <p className="mt-14 font-serif italic text-inkmuted text-lg max-w-2xl">
            <Sparkles size={16} className="inline text-sage mr-2 -mt-1" />
            More setups, props, and lighting are being added throughout the year.
          </p>
        </Reveal>
      </section>

      {/* GALLERY */}
      <section className="bg-stone2 py-20 md:py-28">
        <div className="mx-auto max-w-[1500px] px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {GALLERY.map((src, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <Img
                  src={src}
                  alt={`JCP Studio ${i + 1}`}
                  className={`aspect-[3/4] group ${i % 2 ? "md:mt-10" : ""}`}
                  imgClassName="group-hover:scale-105"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-36">
        <Reveal className="mb-14 md:mb-20 grid grid-cols-1 md:grid-cols-12 items-end gap-8">
          <div className="md:col-span-8">
            <p className="overline text-sage mb-5">Packages</p>
            <h2 className="font-serif font-light text-ink text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Rent by the hour, or take the half-day.
            </h2>
          </div>
          <p className="md:col-span-4 text-inksoft leading-relaxed">
            All packages are booked directly through my scheduler. Session fee is due at booking
            to reserve your slot.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {RATES.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <div
                data-testid={`studio-rate-${i}`}
                className={`h-full flex flex-col rounded-tr-[3rem] border p-8 md:p-9 transition-colors ${
                  r.featured
                    ? "bg-sage text-cream border-sage"
                    : "bg-cream border-ink/10 hover:border-sage"
                }`}
              >
                {r.featured && (
                  <span className="self-start overline text-cream/80 mb-4">Most popular</span>
                )}
                <h3
                  className={`font-serif font-light text-3xl ${
                    r.featured ? "text-cream" : "text-ink"
                  }`}
                >
                  {r.name}
                </h3>
                <p
                  className={`mt-2 text-xs uppercase tracking-[0.22em] ${
                    r.featured ? "text-cream/80" : "text-inkmuted"
                  }`}
                >
                  {r.hours}
                </p>
                <p
                  className={`mt-6 font-serif text-5xl md:text-6xl ${
                    r.featured ? "text-cream" : "text-sage"
                  }`}
                >
                  {r.price}
                </p>
                <p
                  data-testid={`studio-paper-addon-${i}`}
                  className={`mt-2 text-xs uppercase tracking-[0.18em] ${
                    r.featured ? "text-cream/75" : "text-inkmuted"
                  }`}
                >
                  {PAPER_ADDON}
                </p>
                <p
                  className={`mt-6 text-sm leading-relaxed ${
                    r.featured ? "text-cream/85" : "text-inksoft"
                  }`}
                >
                  {r.note}
                </p>
                <a
                  href={STUDIO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`studio-book-${i}`}
                  className={`group mt-8 inline-flex items-center gap-2 text-sm font-medium border-b w-fit pb-1 transition-colors ${
                    r.featured
                      ? "text-cream border-cream/50 hover:border-cream"
                      : "text-ink border-ink/30 hover:border-sage hover:text-sage"
                  }`}
                >
                  Book this rental
                  <ArrowUpRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-12 text-center text-sm text-inkmuted max-w-2xl mx-auto">
            Availability is offered on a first-come basis around booked sessions. Reach out if
            you don&rsquo;t see the time you need — I&rsquo;m happy to work with you.
          </p>
        </Reveal>
      </section>

      {/* DISCLAIMER */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28 md:pb-36">
        <Reveal>
          <div
            data-testid="studio-disclaimer"
            className="flex items-start gap-5 rounded-tl-[3rem] rounded-br-[3rem] bg-ink p-9 md:p-12 text-cream"
          >
            <ShieldCheck size={30} className="shrink-0 mt-1 text-sage" strokeWidth={1.5} />
            <div>
              <p className="overline text-cream/60 mb-2">Please note</p>
              <p className="font-serif font-light text-2xl md:text-3xl leading-snug">
                A signed contract and retainer are required upfront for each rental to secure
                your date.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="bg-sagelight py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-xl">
            Ready to create in the studio?
          </h2>
          <div className="flex flex-wrap gap-4">
            <a
              href={STUDIO_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="studio-cta-book"
              className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
            >
              Book the studio
              <ArrowRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <Link
              to="/contact"
              data-testid="studio-cta-contact"
              className="group inline-flex items-center gap-3 rounded-full border border-ink/25 bg-cream/80 px-9 py-4 text-sm font-medium text-ink hover:border-ink transition-colors"
            >
              Ask a question
              <ArrowUpRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
